import React from "react";
import Box from "@mui/material/Box";
import { styled, useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Link from "@mui/material/Link";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

import logo from "../../assets/logos/logo.png";
import navConfig from "./config";

const Header = () => {
  const theme = useTheme();

  // =========================
  // DESKTOP MENU STATE
  // =========================
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeMenu, setActiveMenu] = React.useState("");

  const handleMenuOpen = (menuName) => (event) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuName);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu("");
  };

  // =========================
  // MOBILE DRAWER STATE
  // =========================
  const [state, setState] = React.useState({
    right: false,
  });

  const handleDrawerClose = () => {
    setState({ right: false });
  };

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  // =========================
  // MOBILE MENU LIST
  // =========================
  const list = (anchor) => (
    <Box
      sx={{
        width: anchor === "top" || anchor === "bottom" ? "auto" : 250,
      }}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <List>
        {navConfig.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton component="a" href={item.path}>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // =========================
  // DRAWER HEADER
  // =========================
  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  }));

  // =========================
  // MENU STYLES
  // =========================
  const menuProps = {
    PaperProps: {
      elevation: 3,
      sx: {
        mt: 1.5,
        minWidth: 220,
        borderRadius: 2,
      },
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    MenuListProps: {
      onMouseLeave: handleMenuClose,
    },
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      marginBottom={{ xs: 2, sm: 0 }}
      marginTop={{ xs: 2, sm: 0 }}
      id="back-to-top-anchor"
    >
      {/* =========================
          LOGO
      ========================= */}
      <Box sx={{ display: { xs: "none", md: "flex" } }} width={130}>
        <Box component="a" href="/" title="ACAwards">
          <img src={logo} alt="logo" width="80%" />
        </Box>
      </Box>

      {/* =========================
          NAVIGATION
      ========================= */}
      <Box
        display="flex"
        alignItems="center"
        width={{ xs: "100%", sm: "50%" }}
        justifyContent="space-between"
      >
        {/* =========================
            DESKTOP MENU
        ========================= */}
        <Box
          sx={{ display: { xs: "none", md: "flex" } }}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          fontWeight={600}
        >
          {/* HOME */}
          <Box>
            <Link underline="none" href="/" color="#D7A036" fontWeight={700}>
              Home
            </Link>
          </Box>

          {/* AWARDS */}
          <Box onMouseLeave={handleMenuClose}>
            <Link
              id="awards-button"
              underline="none"
              color="#D7A036"
              fontWeight={700}
              sx={{ cursor: "pointer" }}
              onMouseEnter={handleMenuOpen("awards")}
            >
              Awards
            </Link>

            <Menu
              id="awards-menu"
              anchorEl={anchorEl}
              open={activeMenu === "awards"}
              onClose={handleMenuClose}
              {...menuProps}
            >
              <MenuItem
                component="a"
                href="/the-award"
                onClick={handleMenuClose}
              >
                The Award
              </MenuItem>

              <MenuItem
                component="a"
                href="/award-categories"
                onClick={handleMenuClose}
              >
                Award Categories
              </MenuItem>
            </Menu>
          </Box>

          {/* NOMINEES */}
          <Box onMouseLeave={handleMenuClose}>
            <Link
              id="nominees-button"
              underline="none"
              color="#D7A036"
              fontWeight={700}
              sx={{ cursor: "pointer" }}
              onMouseEnter={handleMenuOpen("nominees")}
            >
              Nominees
            </Link>

            <Menu
              id="nominees-menu"
              anchorEl={anchorEl}
              open={activeMenu === "nominees"}
              onClose={handleMenuClose}
              {...menuProps}
            >
              <MenuItem
                component="a"
                href="/2023-shortlisted-nominees"
                onClick={handleMenuClose}
              >
                2023 Shortlisted Nominees
              </MenuItem>

              <MenuItem
                component="a"
                href="/2022-shortlisted-nominees"
                onClick={handleMenuClose}
              >
                2022 Shortlisted Nominees
              </MenuItem>

              <MenuItem
                component="a"
                href="/our-judges"
                onClick={handleMenuClose}
              >
                Our Judges
              </MenuItem>
            </Menu>
          </Box>

          {/* WINNERS */}
          <Box onMouseLeave={handleMenuClose}>
            <Link
              id="winners-button"
              underline="none"
              color="#D7A036"
              fontWeight={700}
              sx={{ cursor: "pointer" }}
              onMouseEnter={handleMenuOpen("winners")}
            >
              Winners
            </Link>

            <Menu
              id="winners-menu"
              anchorEl={anchorEl}
              open={activeMenu === "winners"}
              onClose={handleMenuClose}
              {...menuProps}
            >
              <MenuItem
                component="a"
                href="/2023-aca-winners"
                onClick={handleMenuClose}
              >
                2023 ACA Winners
              </MenuItem>

              <MenuItem
                component="a"
                href="/2022-aca-winners"
                onClick={handleMenuClose}
              >
                2022 ACA Winners
              </MenuItem>
            </Menu>
          </Box>

          {/* BLOG */}
          <Box>
            <Link
              underline="none"
              href="/blog"
              color="#D7A036"
              fontWeight={700}
            >
              Blog
            </Link>
          </Box>
        </Box>

        {/* =========================
            MOBILE MENU
        ========================= */}
        <Box sx={{ display: { xs: "flex", md: "none" } }} width="100%">
          <AppBar
            position="fixed"
            sx={{
              backgroundColor: theme.palette.background.paper,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <Toolbar>
              <Box
                component="a"
                href="/"
                title="ACAwards"
                width={155}
                marginRight={20}
                marginTop={1}
                marginBottom={1}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="logo"
                  sx={{
                    display: "block",
                    width: "90%",
                    height: "10vh",
                  }}
                />
              </Box>

              {["right"].map((anchor) => (
                <React.Fragment key={anchor}>
                  <IconButton onClick={toggleDrawer(anchor, true)}>
                    <MenuIcon fontSize="large" sx={{ color: "#D7A036" }} />
                  </IconButton>

                  <SwipeableDrawer
                    anchor={anchor}
                    open={state[anchor]}
                    onClose={toggleDrawer(anchor, false)}
                    onOpen={toggleDrawer(anchor, true)}
                  >
                    <DrawerHeader>
                      <IconButton onClick={handleDrawerClose}>
                        <CloseIcon sx={{ color: "#D7A036" }} />
                      </IconButton>
                    </DrawerHeader>

                    <Divider />

                    {list(anchor)}

                    <Divider />
                  </SwipeableDrawer>
                </React.Fragment>
              ))}
            </Toolbar>
          </AppBar>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
